import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import t from '../theme';
import { useAuth } from '../context/AuthContext';

const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function SchedulePage() {
  const { isAdmin } = useAuth();
  const [sessions, setSessions]   = useState([]);
  const [students, setStudents]   = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [view,     setView]       = useState('list'); // 'list' | 'calendar'
  const [calDate,  setCalDate]    = useState(new Date());

  const [modalOpen,    setModalOpen]    = useState(false);
  const [editSession,  setEditSession]  = useState(null);
  const [form,         setForm]         = useState({ date_time: '', notes: '', student_ids: [] });
  const [formError,    setFormError]    = useState('');
  const [saving,       setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [attendModal,  setAttendModal]  = useState(null); // session
  const [attendance,   setAttendance]   = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [sessRes] = await Promise.all([axios.get('/api/schedule')]);
      setSessions(sessRes.data);
      if (isAdmin) {
        const sr = await axios.get('/api/students');
        setStudents(sr.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setEditSession(null);
    setForm({ date_time: '', notes: '', student_ids: [] });
    setFormError(''); setModalOpen(true);
  };
  const openEdit = (s) => {
    setEditSession(s);
    const dt = new Date(s.date_time);
    const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setForm({ date_time: local, notes: s.notes || '', student_ids: s.students?.map(x => x.id) || [] });
    setFormError(''); setModalOpen(true);
  };
  const toggleStudent = (id) => setForm(f => ({
    ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter(x => x !== id) : [...f.student_ids, id],
  }));

  const handleSave = async () => {
    setFormError('');
    if (!form.date_time) return setFormError('Date and time are required.');
    setSaving(true);
    try {
      if (editSession) { await axios.put(`/api/schedule/${editSession.id}`, form); }
      else { await axios.post('/api/schedule', form); }
      setModalOpen(false); fetchAll();
    } catch (err) { setFormError(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const openAttendance = (session) => {
    const map = {};
    session.students?.forEach(st => { map[st.id] = st.ScheduleStudent?.attended ?? false; });
    setAttendance(map);
    setAttendModal(session);
  };
  const handleSaveAttendance = async () => {
    await axios.put(`/api/schedule/${attendModal.id}/attendance`, { attendance });
    setAttendModal(null); fetchAll();
  };

  const fmtDT = dt => {
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };
  const isPast = dt => new Date(dt) < new Date();
  const isToday = dt => new Date(dt).toDateString() === new Date().toDateString();
  const isUpcoming = dt => new Date(dt) >= new Date();

  // Calendar helpers
  const calYear = calDate.getFullYear();
  const calMonth = calDate.getMonth();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const sessionsByDay = {};
  sessions.forEach(s => {
    const d = new Date(s.date_time);
    if (d.getFullYear() === calYear && d.getMonth() === calMonth) {
      const day = d.getDate();
      if (!sessionsByDay[day]) sessionsByDay[day] = [];
      sessionsByDay[day].push(s);
    }
  });

  const upcoming = sessions.filter(s => isUpcoming(s.date_time)).length;
  const past = sessions.filter(s => isPast(s.date_time) && !isToday(s.date_time)).length;

  if (loading) return <div style={s.loading}>Loading schedule...</div>;

  return (
    <div>
      <div style={s.topRow}>
        <div>
          <h2 style={s.heading}>{isAdmin ? 'Schedule & Attendance' : 'My Schedule'}</h2>
          <p style={s.subheading}>{upcoming} upcoming · {past} past</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={s.viewToggle}>
            <button style={{ ...s.toggleBtn, ...(view === 'list' ? s.toggleBtnActive : {}) }} onClick={() => setView('list')}>☰ List</button>
            <button style={{ ...s.toggleBtn, ...(view === 'calendar' ? s.toggleBtnActive : {}) }} onClick={() => setView('calendar')}>📅 Calendar</button>
          </div>
          {isAdmin && <button style={s.addBtn} onClick={openAdd}>+ New Session</button>}
        </div>
      </div>

      {/* ── Calendar View ── */}
      {view === 'calendar' && (
        <div style={s.calCard}>
          <div style={s.calHeader}>
            <button style={s.calNavBtn} onClick={() => setCalDate(new Date(calYear, calMonth - 1, 1))}>‹</button>
            <h3 style={s.calTitle}>{MONTH_NAMES[calMonth]} {calYear}</h3>
            <button style={s.calNavBtn} onClick={() => setCalDate(new Date(calYear, calMonth + 1, 1))}>›</button>
          </div>
          <div style={s.calGrid}>
            {DAY_NAMES.map(d => <div key={d} style={s.calDayName}>{d}</div>)}
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const daySessions = sessionsByDay[day] || [];
              const isT = new Date(calYear, calMonth, day).toDateString() === new Date().toDateString();
              return (
                <div key={day} style={{ ...s.calDay, ...(isT ? s.calDayToday : {}), ...(daySessions.length ? s.calDayHasSessions : {}) }}>
                  <span style={s.calDayNum}>{day}</span>
                  {daySessions.map(ss => (
                    <div key={ss.id} style={s.calEvent}
                      onClick={() => isAdmin && openEdit(ss)}>
                      {new Date(ss.date_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── List View ── */}
      {view === 'list' && (
        sessions.length === 0 ? (
          <div style={s.emptyCard}>
            <div style={{ fontSize: '52px', marginBottom: '16px' }}>📅</div>
            <p style={s.emptyTitle}>{isAdmin ? 'No sessions scheduled yet' : 'No sessions scheduled for you'}</p>
            {isAdmin && <button style={s.addBtn} onClick={openAdd}>+ Schedule First Session</button>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {sessions.map(session => {
              const past = isPast(session.date_time) && !isToday(session.date_time);
              const today = isToday(session.date_time);
              return (
                <div key={session.id} style={{ ...s.sessionCard, ...(today ? s.sessionCardToday : past ? s.sessionCardPast : {}) }}>
                  <div style={s.sessionLeft}>
                    <div style={{ ...s.sessionDot, background: today ? '#27ae60' : past ? '#4a4845' : '#c49a3c' }} />
                    <div>
                      <div style={s.sessionTime}>{fmtDT(session.date_time)}</div>
                      {today && <span style={s.todayBadge}>Today</span>}
                      {session.notes && <div style={s.sessionNotes}>{session.notes}</div>}
                      <div style={s.sessionStudents}>
                        {session.students?.map(st => (
                          <span key={st.id} style={{ ...s.studentChip, ...(st.ScheduleStudent?.attended ? s.studentChipPresent : {}) }}>
                            {st.full_name}
                            {isAdmin && <span style={{ marginLeft: '4px' }}>{st.ScheduleStudent?.attended ? '✓' : '–'}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <div style={s.sessionActions}>
                      <button style={s.attendBtn} onClick={() => openAttendance(session)}>✓ Attendance</button>
                      <button style={s.editBtn} onClick={() => openEdit(session)}>✏️</button>
                      <button style={s.delBtn} onClick={() => setDeleteTarget(session)}>🗑</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{editSession ? 'Edit Session' : 'New Session'}</h3>
              <button style={s.closeBtn} onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.fieldGroup}>
                <label style={s.label}>Date & Time *</label>
                <input style={s.input} type="datetime-local" value={form.date_time}
                  onChange={e => setForm(f => ({ ...f, date_time: e.target.value }))} />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>Notes</label>
                <textarea style={s.textarea} rows={2} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Session topic, location, or reminders..." />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>
                  Students
                  {form.student_ids.length > 0 && <span style={s.selectedCount}>{form.student_ids.length} selected</span>}
                </label>
                <div style={s.studentGrid}>
                  {students.map(st => {
                    const on = form.student_ids.includes(st.id);
                    return (
                      <div key={st.id}
                        style={{ ...s.studentToggle, ...(on ? s.studentToggleOn : {}) }}
                        onClick={() => toggleStudent(st.id)}>
                        <div style={s.studentAvatar}>{st.full_name[0]}</div>
                        <span style={s.studentName}>{st.full_name}</span>
                        <span style={{ color: '#c49a3c', fontWeight: 800, opacity: on ? 1 : 0 }}>✓</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {formError && <div style={s.errorBox}>⚠️ {formError}</div>}
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setModalOpen(false)}>Cancel</button>
              <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editSession ? 'Save Changes' : 'Schedule Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {attendModal && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setAttendModal(null); }}>
          <div style={{ ...s.modal, maxWidth: '440px' }}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Attendance</h3>
              <button style={s.closeBtn} onClick={() => setAttendModal(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ fontSize: '14px', color: '#6b6966', margin: '0 0 12px' }}>{fmtDT(attendModal.date_time)}</p>
              {attendModal.students?.length === 0 ? (
                <p style={{ color: '#6b6966', fontStyle: 'italic' }}>No students assigned to this session.</p>
              ) : attendModal.students?.map(st => (
                <div key={st.id}
                  style={{ ...s.attendRow, ...(attendance[st.id] ? s.attendRowPresent : {}) }}
                  onClick={() => setAttendance(prev => ({ ...prev, [st.id]: !prev[st.id] }))}>
                  <div style={s.studentAvatar}>{st.full_name[0]}</div>
                  <span style={{ flex: 1, fontWeight: 600, color: '#f0ede8' }}>{st.full_name}</span>
                  <span style={{ fontSize: '20px' }}>{attendance[st.id] ? '✅' : '⬜'}</span>
                </div>
              ))}
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setAttendModal(null)}>Cancel</button>
              <button style={s.saveBtn} onClick={handleSaveAttendance}>Save Attendance</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setDeleteTarget(null); }}>
          <div style={{ ...s.modal, maxWidth: '420px' }}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Delete Session</h3>
              <button style={s.closeBtn} onClick={() => setDeleteTarget(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
                <p style={{ fontSize: '15px', color: '#a8a49e' }}>Delete this session on <strong>{fmtDT(deleteTarget.date_time)}</strong>?</p>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button style={{ ...s.saveBtn, background: '#e74c3c', boxShadow: 'none' }} onClick={async () => {
                await axios.delete(`/api/schedule/${deleteTarget.id}`);
                setDeleteTarget(null); fetchAll();
              }}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  loading: { padding: '48px', textAlign: 'center', color: '#6b6966' },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  heading: { fontSize: '24px', fontWeight: 800, color: '#f0ede8', margin: 0 },
  subheading: { fontSize: '14px', color: '#6b6966', margin: '4px 0 0' },
  addBtn: { background: '#c49a3c', color: '#161614', border: 'none', padding: '11px 22px', borderRadius: '12px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' },
  viewToggle: { display: 'flex', background: '#1c1b19', borderRadius: '10px', padding: '3px', gap: '2px' },
  toggleBtn: { background: 'none', border: 'none', padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: '#6b6966', fontFamily: 'inherit' },
  toggleBtnActive: { background: '#161614', color: '#c49a3c', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' },
  emptyCard: { background: '#161614', borderRadius: '20px', border: '1px solid #2a2927', padding: '48px', textAlign: 'center' },
  emptyTitle: { fontSize: '20px', fontWeight: 800, color: '#f0ede8', margin: '0 0 16px' },
  sessionCard: { background: '#161614', borderRadius: '14px', border: '1px solid #2a2927', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', flexWrap: 'wrap' },
  sessionCardToday: { border: '2px solid #27ae60', background: '#f0fdf4' },
  sessionCardPast: { opacity: 0.65 },
  sessionLeft: { display: 'flex', gap: '14px', flex: 1, minWidth: '200px' },
  sessionDot: { width: '12px', height: '12px', borderRadius: '50%', flexShrink: 0, marginTop: '4px' },
  sessionTime: { fontSize: '15px', fontWeight: 700, color: '#f0ede8' },
  todayBadge: { background: '#27ae60', color: '#161614', padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, display: 'inline-block', marginTop: '4px' },
  sessionNotes: { fontSize: '13px', color: '#6b6966', marginTop: '4px' },
  sessionStudents: { display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '8px' },
  studentChip: { background: '#1c1b19', color: '#c49a3c', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 },
  studentChipPresent: { background: 'rgba(76,175,125,0.1)', color: '#4caf7d' },
  sessionActions: { display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 },
  attendBtn: { background: 'rgba(76,175,125,0.1)', color: '#4caf7d', border: 'none', borderRadius: '8px', padding: '7px 12px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  editBtn: { background: '#1c1b19', border: 'none', borderRadius: '8px', padding: '7px 10px', cursor: 'pointer', fontSize: '14px' },
  delBtn: { background: 'rgba(224,82,82,0.1)', border: 'none', borderRadius: '8px', padding: '7px 10px', cursor: 'pointer', fontSize: '14px' },
  calCard: { background: '#161614', borderRadius: '20px', border: '1px solid #2a2927', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' },
  calHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  calTitle: { fontSize: '18px', fontWeight: 800, color: '#f0ede8', margin: 0 },
  calNavBtn: { background: '#1c1b19', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '18px', cursor: 'pointer', color: '#c49a3c', fontWeight: 700 },
  calGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' },
  calDayName: { textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#6b6966', padding: '6px 0' },
  calDay: { minHeight: '70px', borderRadius: '8px', padding: '6px', background: '#1c1b19', border: '1px solid #2a2927', display: 'flex', flexDirection: 'column', gap: '3px' },
  calDayToday: { border: '2px solid #2980b9', background: '#1c1b19' },
  calDayHasSessions: { background: '#f0fdf4', border: '1px solid #a9dfbf' },
  calDayNum: { fontSize: '13px', fontWeight: 700, color: '#f0ede8' },
  calEvent: { background: '#c49a3c', color: '#161614', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { background: '#161614', borderRadius: '20px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #2a2927' },
  modalTitle: { margin: 0, fontSize: '18px', fontWeight: 800, color: '#f0ede8' },
  closeBtn: { background: '#1c1b19', border: 'none', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '16px', color: '#c49a3c', fontWeight: 700 },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', borderTop: '1px solid #2a2927' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '7px' },
  label: { fontSize: '12px', fontWeight: 700, color: '#2e6da4', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '8px' },
  selectedCount: { background: '#c49a3c', color: '#161614', borderRadius: '20px', padding: '1px 8px', fontSize: '11px', fontWeight: 700 },
  input: { padding: '10px 14px', borderRadius: '10px', border: '1px solid #2a2927', fontSize: '14px', color: '#f0ede8', background: '#1c1b19', outline: 'none', fontFamily: 'inherit' },
  textarea: { padding: '10px 14px', borderRadius: '10px', border: '1px solid #2a2927', fontSize: '14px', color: '#f0ede8', background: '#1c1b19', outline: 'none', fontFamily: 'inherit', resize: 'vertical' },
  studentGrid: { display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflowY: 'auto' },
  studentToggle: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', border: '1px solid #2a2927', background: '#1c1b19', cursor: 'pointer' },
  studentToggleOn: { border: '2px solid #2980b9', background: '#1c1b19' },
  studentAvatar: { width: '30px', height: '30px', borderRadius: '50%', background: '#c49a3c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, color: '#161614', flexShrink: 0 },
  studentName: { fontSize: '14px', fontWeight: 600, color: '#f0ede8', flex: 1 },
  errorBox: { background: 'rgba(224,82,82,0.1)', border: '1px solid #ffcdd2', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', color: '#e05252' },
  cancelBtn: { background: '#1c1b19', color: '#c49a3c', border: '1px solid #b8d9f0', padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  saveBtn: { background: '#c49a3c', color: '#161614', border: 'none', padding: '10px 22px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' },
  attendRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', border: '1px solid #2a2927', background: '#1c1b19', cursor: 'pointer', marginBottom: '6px' },
  attendRowPresent: { border: '2px solid #27ae60', background: '#f0fdf4' },
};
